import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Plus, Edit2, Trash2, Image as ImageIcon, Upload } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORIES = [
  { id: 'ilot', label: 'Îlot de Bushumba' },
  { id: 'activites', label: 'Activités' },
  { id: 'formations', label: 'Formations FPT' },
  { id: 'agriculture', label: 'Agri-élevage' },
  { id: 'communaute', label: 'Communauté' },
];

const EMPTY_FORM = { title: '', caption: '', category: 'activites', image_url: '' };

const resolveImageUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  return `${BACKEND_URL}${url}`;
};

export default function PhotosManager() {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const fetchPhotos = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/photos`);
      setPhotos(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchPhotos(); }, [fetchPhotos]);

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error('La photo ne doit pas dépasser 8 Mo');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/files/upload`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setFormData({ ...formData, image_url: res.data.url });
      toast.success('Photo chargée');
    } catch (error) { toast.error('Erreur lors du chargement'); }
    finally { setUploading(false); if (fileInputRef.current) fileInputRef.current.value = ''; }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.image_url) {
      toast.error('Veuillez charger une photo');
      return;
    }
    try {
      if (editingId) {
        await axios.put(`${API}/admin/photos/${editingId}`, formData, { withCredentials: true });
        toast.success('Photo mise à jour');
      } else {
        await axios.post(`${API}/admin/photos`, formData, { withCredentials: true });
        toast.success('Photo ajoutée');
      }
      resetForm();
      fetchPhotos();
    } catch (error) { toast.error('Erreur'); }
  };

  const handleEdit = (photo) => {
    setEditingId(photo.photo_id);
    setFormData({
      title: photo.title,
      caption: photo.caption || '',
      category: photo.category,
      image_url: photo.image_url
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette photo ?')) return;
    try {
      await axios.delete(`${API}/admin/photos/${id}`, { withCredentials: true });
      toast.success('Photo supprimée');
      fetchPhotos();
    } catch (error) { toast.error('Erreur'); }
  };

  const resetForm = () => {
    setFormData(EMPTY_FORM);
    setEditingId(null);
    setShowForm(false);
  };

  return (
    <div data-testid="photos-manager">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-[#2E332D]" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
          Galerie photos ({photos.length})
        </h2>
        <button onClick={() => setShowForm(!showForm)} className="btn-primary inline-flex items-center" data-testid="add-photo-button">
          <Plus size={20} className="mr-2" />
          {showForm ? 'Annuler' : 'Ajouter une photo'}
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="card mb-6" data-testid="photo-form">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-bold text-[#2E332D] mb-1">Titre *</label>
              <input
                type="text" required value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg"
                data-testid="photo-title-input"
              />
            </div>
            <div>
              <label className="block text-sm font-bold text-[#2E332D] mb-1">Légende (optionnel)</label>
              <textarea
                rows={2} value={formData.caption}
                onChange={(e) => setFormData({ ...formData, caption: e.target.value })}
                className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg"
                data-testid="photo-caption-input"
              />
            </div>
            <div>
              <label className="block text-sm font-bold text-[#2E332D] mb-1">Catégorie *</label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg"
                data-testid="photo-category-select"
              >
                {CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
            </div>
            <div className="border-2 border-dashed border-[#E5E3DB] rounded-lg p-4">
              <label className="flex items-center gap-2 text-sm font-bold text-[#2E332D] mb-2">
                <Upload size={16} /> Charger une photo (max 8 Mo)
              </label>
              <input
                ref={fileInputRef} type="file" accept="image/*"
                onChange={handleUpload} disabled={uploading}
                className="text-sm" data-testid="photo-upload"
              />
              {uploading && <p className="text-xs text-[#5C665A] mt-1">Chargement...</p>}
              {formData.image_url && (
                <img
                  src={resolveImageUrl(formData.image_url)}
                  alt="Aperçu"
                  className="mt-3 max-h-40 rounded border border-[#E5E3DB]"
                />
              )}
            </div>
            <div className="flex gap-2">
              <button type="submit" className="btn-primary" data-testid="submit-photo-button">
                {editingId ? 'Mettre à jour' : 'Ajouter'}
              </button>
              <button type="button" onClick={resetForm} className="btn-secondary">Annuler</button>
            </div>
          </div>
        </form>
      )}

      {loading ? (
        <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#4A6B48]"></div></div>
      ) : photos.length === 0 ? (
        <p className="text-center text-[#5C665A] py-8">Aucune photo. Cliquez sur &quot;Ajouter une photo&quot;.</p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {photos.map((photo) => (
            <div key={photo.photo_id} className="card p-2" data-testid={`admin-photo-${photo.photo_id}`}>
              <div className="aspect-square overflow-hidden rounded mb-2 bg-[#F9F8F6]">
                <img
                  src={resolveImageUrl(photo.image_url)}
                  alt={photo.title}
                  className="w-full h-full object-cover"
                />
              </div>
              <p className="text-xs font-bold text-[#2E332D] line-clamp-1">{photo.title}</p>
              <p className="text-xs text-[#5C665A]">
                {CATEGORIES.find((c) => c.id === photo.category)?.label || photo.category}
              </p>
              <div className="flex justify-end gap-1 mt-2">
                <button onClick={() => handleEdit(photo)} className="p-1 hover:bg-gray-100 rounded">
                  <Edit2 size={14} className="text-[#4A6B48]" />
                </button>
                <button onClick={() => handleDelete(photo.photo_id)} className="p-1 hover:bg-gray-100 rounded">
                  <Trash2 size={14} className="text-red-500" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
